import { Box, FormControl, MenuItem, Select, TextField } from "@mui/material"
import type { FC } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { TEST_TEMPLATE_RESULT_UOM_OPTIONS } from "../../constants"
import type { TestTemplateFormValues } from "../../types"
import { getTestTemplateComparisonLabel, getTestTemplateResultUomLabel } from "../../utils"

import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import { TestTemplateComparison } from "@/graphql/codegen/graphql"

const TestTemplateForm: FC = () => {
  const { control } = useFormContext<TestTemplateFormValues>()

  return (
    <Box className='flex flex-col gap-6'>
      <Controller
        name='description'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box>
            <label htmlFor='description' className={FIELD_LABEL_CLASSES}>
              Description<span className='text-red-600'>*</span>
            </label>
            <TextField
              {...field}
              {...TEXT_FIELD_STYLING_PROPS}
              id='description'
              data-testid='description-input'
              placeholder='Add Test Description'
              error={!!error}
            />
            {error?.message && (
              <p className={FIELD_ERROR_CLASSES} data-testid='description-error'>
                {error.message}
              </p>
            )}
          </Box>
        )}
      />
      <Controller
        name='resultsUnitOfMeasure'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth>
            <label id='results-uom-label' className={FIELD_LABEL_CLASSES}>
              Results UoM
            </label>
            <Select
              {...SELECT_STYLING_PROPS}
              {...field}
              data-testid='results-uom-select'
              labelId='results-uom-label'
              error={!!error}
              renderValue={(selected) =>
                selected ? (
                  getTestTemplateResultUomLabel(selected)
                ) : (
                  <span className='text-gray-500'>Select Unit of Measure</span>
                )
              }
            >
              {TEST_TEMPLATE_RESULT_UOM_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} data-test='results-uom-option' value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {error?.message && (
              <p className={FIELD_ERROR_CLASSES} data-testid='results-uom-error'>
                {error.message}
              </p>
            )}
          </FormControl>
        )}
      />
      <Controller
        name='comparison'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth>
            <label id='comparison-label' className={FIELD_LABEL_CLASSES}>
              Comparison<span className='text-red-600'>*</span>
            </label>
            <Select
              {...field}
              {...SELECT_STYLING_PROPS}
              data-testid='comparison-select'
              labelId='comparison-label'
              error={!!error}
              renderValue={(selected) =>
                selected ? (
                  getTestTemplateComparisonLabel(selected as TestTemplateComparison)
                ) : (
                  <span className='text-gray-500'>Select Comparison</span>
                )
              }
            >
              {Object.keys(TestTemplateComparison).map((key) => (
                <MenuItem
                  key={key}
                  data-test='comparison-option'
                  value={TestTemplateComparison[key as keyof typeof TestTemplateComparison]}
                >
                  {getTestTemplateComparisonLabel(TestTemplateComparison[key as keyof typeof TestTemplateComparison])}
                </MenuItem>
              ))}
            </Select>
            {error?.message && (
              <p className={FIELD_ERROR_CLASSES} data-testid='comparison-error'>
                {error.message}
              </p>
            )}
          </FormControl>
        )}
      />
    </Box>
  )
}

export default TestTemplateForm
